import { forwardRef } from "react";
import { QRCode } from "antd";
import { ACORN_BRAND } from "./acornQuoteData";
import { formatAcornMoney, formatAcornQuantity } from "./acornQuoteFormat";
import "./acornQuotePoster.css";

const QR_SIZE = 104;

function ClassicAcornPoster({ quoteDisplay, t, locale, posterRef }) {
    const {
        projectName,
        quoteDateLabel,
        validUntilLabel,
        currency,
        portfolioUrl,
        hideSubitemPrices,
        categories,
        grandTotal,
        globalNotes,
    } = quoteDisplay;

    const metaParts = [quoteDateLabel];
    if (validUntilLabel) {
        metaParts.push(`${t("poster.validUntil")} ${validUntilLabel}`);
    }

    return (
        <div ref={posterRef} className="acorn-quote-poster acorn-quote-poster--classic">
            <div className="acorn-classic__inner">
                <header className="acorn-classic__header">
                    <div className="acorn-classic__brand">{ACORN_BRAND}</div>
                    <div className="acorn-classic__doc-title">
                        {t("poster.title")}
                        <span className="acorn-classic__doc-title-en">
                            {locale === "en" ? t("poster.titleEn") : "QUOTATION"}
                        </span>
                    </div>
                    <h1 className="acorn-classic__project">
                        {projectName || t("poster.projectPlaceholder")}
                    </h1>
                    {metaParts.filter(Boolean).length > 0 && (
                        <div className="acorn-classic__meta">{metaParts.filter(Boolean).join(" · ")}</div>
                    )}
                </header>

                <section className="acorn-classic__body">
                    {categories.map((category, categoryIndex) => (
                        <div key={category.key} className="acorn-classic__section">
                            <div className="acorn-classic__section-title">
                                <span className="acorn-classic__section-index">
                                    {String(categoryIndex + 1).padStart(2, "0")}
                                </span>
                                <span>{t(`categories.${category.key}`)}</span>
                            </div>

                            {category.items.map((item) => (
                                <div key={item.id} className="acorn-classic__row">
                                    <div className="acorn-classic__row-top">
                                        <span className="acorn-classic__item-name">{item.name}</span>
                                        {!hideSubitemPrices && (
                                            <span className="acorn-classic__item-amount">
                                                {formatAcornMoney(item.subtotal, currency)}
                                            </span>
                                        )}
                                    </div>
                                    {!hideSubitemPrices && (
                                        <div className="acorn-classic__item-calc">
                                            {formatAcornMoney(item.unitPrice, currency)} ×{" "}
                                            {formatAcornQuantity(item.quantity)}
                                        </div>
                                    )}
                                    {item.note ? (
                                        <div className="acorn-classic__item-note">{item.note}</div>
                                    ) : null}
                                </div>
                            ))}

                            {!hideSubitemPrices && (
                                <div className="acorn-classic__section-subtotal">
                                    <span>{t("poster.sectionSubtotal")}</span>
                                    <span>{formatAcornMoney(category.subtotal, currency)}</span>
                                </div>
                            )}
                        </div>
                    ))}
                </section>

                <section className="acorn-classic__total">
                    <span className="acorn-classic__total-label">{t("poster.grandTotal")}</span>
                    <span className="acorn-classic__total-amount">
                        {formatAcornMoney(grandTotal, currency)}
                    </span>
                </section>

                <footer className="acorn-classic__footer">
                    {globalNotes.length > 0 && (
                        <div className="acorn-classic__notes">
                            {globalNotes.map((note, index) => (
                                <div key={`acorn-note-${index}`} className="acorn-classic__note-line">
                                    {globalNotes.length > 1
                                        ? locale === "zh" || locale === "en"
                                            ? `${index + 1}. `
                                            : "• "
                                        : ""}
                                    {note}
                                </div>
                            ))}
                        </div>
                    )}

                    {portfolioUrl ? (
                        <div className="acorn-classic__qr-row">
                            <div className="acorn-classic__qr-wrapper">
                                <div className="acorn-classic__qr-inner">
                                    <QRCode
                                        value={portfolioUrl}
                                        size={QR_SIZE}
                                        color="#1d1d1f"
                                        bgColor="#ffffff"
                                        bordered={false}
                                        errorLevel="M"
                                    />
                                </div>
                            </div>
                            <div className="acorn-classic__qr-text">
                                <div>{t("poster.scanZh")}</div>
                                <div>{t("poster.scanEn")}</div>
                            </div>
                        </div>
                    ) : null}

                    <div className="acorn-classic__divider" />
                    <div className="acorn-classic__social">{t("poster.social")}</div>
                    <div className="acorn-classic__website">{ACORN_BRAND}</div>
                </footer>
            </div>
        </div>
    );
}

function EditorialAcornPoster({ quoteDisplay, t, locale, posterRef }) {
    const {
        projectName,
        quoteDateLabel,
        validUntilLabel,
        currency,
        portfolioUrl,
        hideSubitemPrices,
        categories,
        grandTotal,
        globalNotes,
    } = quoteDisplay;

    const metaParts = [quoteDateLabel];
    if (validUntilLabel) {
        metaParts.push(`${t("poster.validUntil")} ${validUntilLabel}`);
    }

    return (
        <div ref={posterRef} className="acorn-quote-poster acorn-quote-poster--editorial">
            <div className="acorn-editorial__inner">
                <header className="acorn-editorial__header">
                    <div className="acorn-editorial__brand">{ACORN_BRAND}</div>
                    <div className="acorn-editorial__doc">
                        {locale === "en" ? t("poster.titleEn") : "QUOTATION"}
                    </div>
                </header>

                <div className="acorn-editorial__project-row">
                    <div className="acorn-editorial__meta">
                        {metaParts.filter(Boolean).length > 0 ? (
                            metaParts.filter(Boolean).join("  ·  ")
                        ) : (
                            <span>{locale === "en" ? "ESTIMATE" : "报价单"}</span>
                        )}
                    </div>
                    <h1 className="acorn-editorial__project">
                        {projectName || t("poster.projectPlaceholder")}
                    </h1>
                </div>

                <main className="acorn-editorial__body">
                    {categories.map((category, categoryIndex) => (
                        <section key={category.key} className="acorn-editorial__section">
                            <header className="acorn-editorial__section-head">
                                <span className="acorn-editorial__section-index">
                                    {String(categoryIndex + 1).padStart(2, "0")}
                                </span>
                                <h2 className="acorn-editorial__section-title">
                                    {t(`categories.${category.key}`)}
                                </h2>
                                {!hideSubitemPrices && (
                                    <span className="acorn-editorial__section-subtotal">
                                        {formatAcornMoney(category.subtotal, currency)}
                                    </span>
                                )}
                            </header>

                            {category.items.map((item) => (
                                <div key={item.id} className="acorn-editorial__item">
                                    <div className="acorn-editorial__item-copy">
                                        <span className="acorn-editorial__item-name">{item.name}</span>
                                        {!hideSubitemPrices && (
                                            <span className="acorn-editorial__item-calc">
                                                {formatAcornMoney(item.unitPrice, currency)} ×{" "}
                                                {formatAcornQuantity(item.quantity)}
                                            </span>
                                        )}
                                        {item.note ? (
                                            <span className="acorn-editorial__item-note">{item.note}</span>
                                        ) : null}
                                    </div>
                                    {!hideSubitemPrices && (
                                        <div className="acorn-editorial__item-amount">
                                            {formatAcornMoney(item.subtotal, currency)}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </section>
                    ))}
                </main>

                <footer className="acorn-editorial__footer">
                    {globalNotes.length > 0 && (
                        <div className="acorn-editorial__notes">
                            <div className="acorn-editorial__notes-title">{t("poster.notesTitle")}</div>
                            {globalNotes.map((note, index) => (
                                <p key={`acorn-editorial-note-${index}`} className="acorn-editorial__note">
                                    {globalNotes.length > 1 ? `${index + 1}. ` : ""}
                                    {note}
                                </p>
                            ))}
                        </div>
                    )}

                    <div className="acorn-editorial__total">
                        <span>{t("poster.grandTotal")}</span>
                        <strong>{formatAcornMoney(grandTotal, currency)}</strong>
                    </div>

                    <div className="acorn-editorial__contact">
                        <div className="acorn-editorial__qr">
                            {portfolioUrl ? (
                                <QRCode
                                    value={portfolioUrl}
                                    size={96}
                                    color="#15130f"
                                    bgColor="#f7f3ec"
                                    bordered={false}
                                    errorLevel="M"
                                />
                            ) : (
                                <span className="acorn-editorial__qr-empty" />
                            )}
                        </div>
                        <div className="acorn-editorial__contact-copy">
                            <div className="acorn-editorial__handle">{t("poster.social")}</div>
                            <div className="acorn-editorial__scan">{t("poster.scanEn")}</div>
                            <div className="acorn-editorial__footer-brand">{ACORN_BRAND}</div>
                        </div>
                    </div>
                </footer>
            </div>
        </div>
    );
}

const AcornQuotePoster = forwardRef(function AcornQuotePoster(
    { quoteDisplay, style = "classic", t, locale },
    ref
) {
    if (!quoteDisplay) {
        return <div ref={ref} />;
    }

    return style === "editorial" ? (
        <EditorialAcornPoster quoteDisplay={quoteDisplay} t={t} locale={locale} posterRef={ref} />
    ) : (
        <ClassicAcornPoster quoteDisplay={quoteDisplay} t={t} locale={locale} posterRef={ref} />
    );
});

export default AcornQuotePoster;
